import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import * as React from 'react';

import { PaginationButtons } from '@/components/admin/admin-inquiries-table/components/PaginationButtons';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/design-system';
import {
  SIZE_SCALE,
  COLOR_SCALE,
  GAP_SCALE,
  PADDING_X,
  PADDING_Y,
  PADDING_TOP,
  POSITION_SCALE,
  MARGIN_X,
  MARGIN_LEFT,
  RADIUS_SCALE,
  MARGIN_TOP,
  SHADOW_SCALE,
  SURFACE_PADDING,
  TYPE_SCALE,
  FONT_WEIGHTS,
  Z_INDEX,
  ACCESSIBILITY_SCALE,
} from '@/components/design-system/tokens';
import { buttonVariants } from '@/components/design-system/variants';
import { withMemoryLeakMonitor } from '@/components/ui/MemoryLeakMonitor';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

// Helper functions to reduce component complexity
const getContainerClasses = (variant: string) => {
  switch (variant) {
    case 'card':
      return `${COLOR_SCALE.surface} ${RADIUS_SCALE['2xl']} ${SHADOW_SCALE.lg} ${SURFACE_PADDING['6']}`;
    case 'minimal':
      return '';
    case 'admin':
      return `flex flex-col ${GAP_SCALE['3']} sm:${GAP_SCALE['4']} ${PADDING_X['3']} sm:${PADDING_X['6']} ${PADDING_Y['3']} sm:${PADDING_Y['5']} border-t ${COLOR_SCALE['admin-border']}`;
    case 'default':
    default:
      return '';
  }
};

const getLayoutClasses = (layout: string) => {
  switch (layout) {
    case 'centered':
      return `flex-col sm:flex-row items-center justify-center ${GAP_SCALE['4']}`;
    case 'left-aligned':
      return `flex-col sm:flex-row items-start sm:items-center justify-start ${GAP_SCALE['4']}`;
    case 'space-between':
      return `flex-col sm:flex-row items-center justify-between ${GAP_SCALE['4']}`;
    case 'admin-table':
      return `flex flex-col sm:flex-row sm:items-center sm:justify-between ${GAP_SCALE['2']} sm:${GAP_SCALE['3']}`;
    default:
      return `flex-col sm:flex-row items-center justify-center ${GAP_SCALE['4']}`;
  }
};

interface PaginationProps extends React.HTMLAttributes<HTMLElement> {
  className?: string;
}

const Pagination = ({ className, ...props }: PaginationProps) => {
  const { t } = useTranslation();
  return (
    <nav
      role="navigation"
      aria-label={t('aria', 'pagination_navigation') || 'Pagination navigation'}
      className={cn(`${MARGIN_X.auto} flex ${SIZE_SCALE.full} justify-center`, className)}
      {...props}
    />
  );
};
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn(`flex flex-row items-center ${GAP_SCALE.xs}`, className)}
    {...props}
  />
));
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<HTMLLIElement, React.HTMLAttributes<HTMLLIElement>>(
  ({ className, ...props }, ref) => <li ref={ref} className={cn('', className)} {...props} />
);
PaginationItem.displayName = 'PaginationItem';

interface PaginationLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  isActive?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'default' | 'icon';
  radius?: keyof typeof RADIUS_SCALE;
  disabled?: boolean;
}

const PaginationLink = React.forwardRef<HTMLAnchorElement, PaginationLinkProps>(
  ({ className, isActive, size = 'icon', radius = 'xl', children, ...props }, ref) => (
    <a
      ref={ref}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        buttonVariants({
          variant: isActive ? 'paginationActive' : 'pagination',
          size: size === 'icon' ? 'sm' : size,
        }),
        Reflect.get(RADIUS_SCALE, radius) || RADIUS_SCALE.xl,
        size === 'icon' ? `${SIZE_SCALE[10]} ${SIZE_SCALE.w10} ${PADDING_X[0]}` : '',
        className
      )}
      {...props}
    >
      {children}
    </a>
  )
);
PaginationLink.displayName = 'PaginationLink';

interface PaginationPreviousProps extends Omit<PaginationLinkProps, 'children'> {
  className?: string;
}

const PaginationPrevious = ({ className, ...props }: PaginationPreviousProps) => {
  const { t } = useTranslation();
  return (
    <PaginationLink
      aria-label={t('common', 'previous_page') || 'Go to previous page'}
      size="sm"
      className={cn(`${GAP_SCALE.xs} ${POSITION_SCALE.left[2]}`, className)}
      {...props}
    >
      <ChevronLeft className={`${SIZE_SCALE['4']} ${SIZE_SCALE.w4}`} />
      <span>{t('common', 'previous') || 'Previous'}</span>
    </PaginationLink>
  );
};
PaginationPrevious.displayName = 'PaginationPrevious';

interface PaginationNextProps extends Omit<PaginationLinkProps, 'children'> {
  className?: string;
}

const PaginationNext = ({ className, ...props }: PaginationNextProps) => {
  const { t } = useTranslation();
  return (
    <PaginationLink
      aria-label={t('common', 'next_page') || 'Go to next page'}
      size="sm"
      className={cn(`${GAP_SCALE.xs} ${POSITION_SCALE.right[2]}`, className)}
      {...props}
    >
      <span>{t('common', 'next') || 'Next'}</span>
      <ChevronRight className={`${SIZE_SCALE['4']} ${SIZE_SCALE.w4}`} />
    </PaginationLink>
  );
};
PaginationNext.displayName = 'PaginationNext';

interface PaginationEllipsisProps extends React.HTMLAttributes<HTMLSpanElement> {
  className?: string;
}

const PaginationEllipsis = ({ className, ...props }: PaginationEllipsisProps) => {
  const { t } = useTranslation();
  return (
    <span
      aria-hidden
      className={cn(
        `flex ${SIZE_SCALE['9']} ${SIZE_SCALE.w9} items-center justify-center`,
        className
      )}
      {...props}
    >
      <MoreHorizontal className={`${SIZE_SCALE['4']} ${SIZE_SCALE.w4}`} />
      <span className={`${ACCESSIBILITY_SCALE.srOnly}`}>
        {t('common', 'show_more') || 'More pages'}
      </span>
    </span>
  );
};
PaginationEllipsis.displayName = 'PaginationEllipsis';

interface PaginationComponentProps extends React.HTMLAttributes<HTMLElement> {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  showFirstLast?: boolean;
  maxPagesToShow?: number;
  showKeyboardHint?: boolean;
  setShowKeyboardHint?: ((show: boolean) => void) | undefined;
}

/**
 * Complete Pagination Component with built-in logic
 * Handles page number generation, ellipsis, and keyboard navigation
 */
const PaginationComponent = ({
  currentPage,
  totalPages,
  onPageChange,
  showFirstLast = true,
  maxPagesToShow = 5,
  className,
  showKeyboardHint = false,
  setShowKeyboardHint,
  ...props
}: PaginationComponentProps) => {
  const { t } = useTranslation();

  // Generate page numbers with ellipsis logic
  const pageNumbers = React.useMemo(() => {
    if (totalPages <= 0) return [];
    if (totalPages <= maxPagesToShow) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = [];
    const half = Math.floor(maxPagesToShow / 2);
    let start = Math.max(1, currentPage - half);
    let end = start + maxPagesToShow - 1;

    if (end > totalPages) {
      end = totalPages;
      start = end - maxPagesToShow + 1;
    }

    if (start < 1) {
      start = 1;
      end = maxPagesToShow;
    }

    // Add ellipsis logic
    for (let i = start; i <= end; i++) {
      if (i === start && i > 1) {
        pages.push('ellipsis-start');
      }
      pages.push(i);
      if (i === end && i < totalPages) {
        pages.push('ellipsis-end');
      }
    }

    return pages;
  }, [currentPage, totalPages, maxPagesToShow]);

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && currentPage > 1) {
        onPageChange(currentPage - 1);
      } else if (event.key === 'ArrowRight' && currentPage < totalPages) {
        onPageChange(currentPage + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, totalPages, onPageChange]);

  if (totalPages <= 1) return null;

  const paginationInstructionsId = 'pagination-instructions';

  return (
    <nav
      role="navigation"
      aria-label={t('aria', 'pagination_navigation') || 'Pagination navigation'}
      className={cn(`mx-auto flex ${SIZE_SCALE.full} justify-center`, className)}
      {...props}
    >
      <p className={`${ACCESSIBILITY_SCALE.srOnly}`} id={paginationInstructionsId}>
        {t('common', 'pagination_keyboard_help') ||
          'Press left or right arrow keys to change pages.'}
      </p>

      <PaginationContent>
        {/* First button */}
        {showFirstLast && (
          <PaginationItem>
            <PaginationLink
              size="sm"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              aria-label={t('common', 'first_page') || 'Go to first page'}
            >
              {t('common', 'first') || 'First'}
            </PaginationLink>
          </PaginationItem>
        )}

        {/* Previous button */}
        <PaginationItem>
          <PaginationPrevious
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          />
        </PaginationItem>

        {/* Page numbers */}
        {pageNumbers.map((pageNum, _index) => (
          <PaginationItem key={pageNum}>
            {pageNum === 'ellipsis-start' || pageNum === 'ellipsis-end' ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                size="sm"
                onClick={() => onPageChange(Number(pageNum))}
                isActive={currentPage === Number(pageNum)}
                aria-current={currentPage === Number(pageNum) ? 'page' : undefined}
              >
                {pageNum}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}

        {/* Next button */}
        <PaginationItem>
          <PaginationNext
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          />
        </PaginationItem>

        {/* Last button */}
        {showFirstLast && (
          <PaginationItem>
            <PaginationLink
              size="sm"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
              aria-label={t('common', 'last_page') || 'Go to last page'}
            >
              {t('common', 'last') || 'Last'}
            </PaginationLink>
          </PaginationItem>
        )}
      </PaginationContent>

      {/* Keyboard Navigation Hint */}
      {showKeyboardHint && setShowKeyboardHint && (
        <div
          className={`fixed ${POSITION_SCALE.bottom['4']} ${POSITION_SCALE.left['4']} ${COLOR_SCALE['pagination-hint-bg']} ${COLOR_SCALE['pagination-hint-text']} text-xs ${PADDING_X['4']} ${PADDING_Y['2']} ${RADIUS_SCALE.lg} shadow-lg flex items-center ${GAP_SCALE.sm} whitespace-nowrap z-${Z_INDEX.dropdown}`}
        >
          <span>{t('common', 'keyboard_hint') || 'Use ← → arrow keys to navigate'}</span>
          <button
            onClick={() => setShowKeyboardHint(false)}
            className={`${MARGIN_LEFT['2']} ${COLOR_SCALE['pagination-hint-hover']}`}
          >
            ✕
          </button>
        </div>
      )}
    </nav>
  );
};

PaginationComponent.displayName = 'PaginationComponent';

const PaginationComponentWithMonitor = withMemoryLeakMonitor(PaginationComponent, {
  componentName: 'PaginationComponent',
  enableRenderTracking: true,
  enableLeakDetection: true,
  warnOnExcessiveRenders: true,
  renderThreshold: 10,
});

interface UnifiedPaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  // Core pagination props
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;

  // Items per page functionality
  itemsPerPage?: number;
  onItemsPerPageChange?: (items: number) => void;
  itemsPerPageOptions?: number[];
  showItemsPerPageSelector?: boolean;

  // Admin-specific props
  totalItems?: number;
  startIndex?: number;
  endIndex?: number;
  showDetailedInfo?: boolean;

  // Layout options
  layout?: 'centered' | 'left-aligned' | 'space-between' | 'admin-table';
  showPageInfo?: boolean;

  // Styling variants
  variant?: 'default' | 'card' | 'minimal' | 'admin';
  showFirstLast?: boolean;
  showKeyboardHint?: boolean;
  setShowKeyboardHint?: (show: boolean) => void;
}

/**
 * Unified Pagination Component - combines all pagination features
 * Supports items per page selector, different layouts, and styling variants
 */
const UnifiedPagination = ({
  // Core pagination props
  currentPage,
  totalPages,
  onPageChange,

  // Items per page functionality
  itemsPerPage,
  onItemsPerPageChange,
  itemsPerPageOptions = [6, 9, 12, 18, 24],
  showItemsPerPageSelector = false,

  // Admin-specific props
  totalItems,
  startIndex,
  endIndex,
  showDetailedInfo = false, // Show "showing X to Y of Z items" instead of "Page X of Y"

  // Layout options
  layout = 'centered', // 'centered', 'left-aligned', 'space-between', 'admin-table'
  showPageInfo = true,

  // Styling variants
  variant = 'default', // 'default', 'card', 'minimal', 'admin'
  showFirstLast = true,
  showKeyboardHint = false,
  setShowKeyboardHint,

  // Additional props
  className,
  ...props
}: UnifiedPaginationProps) => {
  const { t } = useTranslation();

  if (totalPages <= 1 && !showItemsPerPageSelector) return null;

  return (
    <div className={cn(`${MARGIN_TOP['12']}`, getContainerClasses(variant), className)} {...props}>
      {/* Admin-style detailed info and items per page selector */}
      {variant === 'admin' && (
        <div className={cn(`flex`, getLayoutClasses('admin-table'))}>
          {showDetailedInfo &&
            totalItems !== undefined &&
            startIndex !== undefined &&
            endIndex !== undefined && (
              <p
                className={`${TYPE_SCALE['2xs']} sm:${TYPE_SCALE.xs} md:${TYPE_SCALE.sm} ${COLOR_SCALE['foreground-muted']}`}
              >
                {t('admin_inquiries', 'showing') || 'Showing'}{' '}
                <span className={`${FONT_WEIGHTS.semibold} ${COLOR_SCALE.foreground}`}>
                  {startIndex + 1}
                </span>{' '}
                {t('admin_inquiries', 'to') || 'to'}{' '}
                <span className={`font-semibold ${COLOR_SCALE.foreground}`}>
                  {Math.min(endIndex, totalItems)}
                </span>{' '}
                {t('admin_inquiries', 'of') || 'of'}{' '}
                <span className={`font-semibold ${COLOR_SCALE.foreground}`}>{totalItems}</span>{' '}
                {t('admin_inquiries', 'inquiries') || 'inquiries'}
              </p>
            )}

          {showItemsPerPageSelector && (
            <div className={`flex items-center justify-between sm:justify-end ${GAP_SCALE['2']}`}>
              <span
                className={`${TYPE_SCALE['3xs']} sm:${TYPE_SCALE.xs} md:${TYPE_SCALE.sm} ${COLOR_SCALE['foreground-muted']}`}
              >
                {t('admin_inquiries', 'show')}
              </span>
              <Select
                value={itemsPerPage?.toString() ?? ''}
                onValueChange={value => {
                  onItemsPerPageChange?.(Number(value));
                  onPageChange(1);
                }}
              >
                <SelectTrigger
                  className={`${SIZE_SCALE['9']} sm:${SIZE_SCALE['8']} md:${SIZE_SCALE['9']} w-full sm:${SIZE_SCALE.w16} md:${SIZE_SCALE.w20} ${RADIUS_SCALE.lg} ${COLOR_SCALE['admin-border']} ${TYPE_SCALE['2xs']} sm:${TYPE_SCALE.xs} md:${TYPE_SCALE.sm}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {itemsPerPageOptions.map(option => (
                    <SelectItem key={option} value={option.toString()} />
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {/* Standard layout for non-admin variants */}
      {variant !== 'admin' && (
        <div className={cn('flex', getLayoutClasses(layout))}>
          {/* Items per page selector */}
          {showItemsPerPageSelector && (
            <div className={`hidden md:flex items-center ${GAP_SCALE['2']}`}>
              <span className={`${TYPE_SCALE.base} ${COLOR_SCALE['foreground-muted']}`}>
                {t('common', 'show_label')}:
              </span>
              <Select
                value={itemsPerPage?.toString() ?? ''}
                onValueChange={value => {
                  onItemsPerPageChange?.(Number(value));
                  onPageChange(1);
                }}
              >
                <SelectTrigger
                  className={`${SIZE_SCALE.w20} ${RADIUS_SCALE.lg} ${COLOR_SCALE['field-border']}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {itemsPerPageOptions.map(option => (
                    <SelectItem key={option} value={option.toString()} />
                  ))}
                </SelectContent>
              </Select>
              <span className={`${TYPE_SCALE.base} ${COLOR_SCALE['foreground-muted']}`}>
                {t('common', 'per_page') || 'per page'}
              </span>
            </div>
          )}

          {/* Page Info */}
          {showPageInfo && totalPages > 1 && (
            <div
              className={`${TYPE_SCALE.sm} ${COLOR_SCALE['foreground-muted']} whitespace-nowrap`}
            >
              {showDetailedInfo &&
              totalItems !== undefined &&
              startIndex !== undefined &&
              endIndex !== undefined ? (
                <>
                  {t('admin_inquiries', 'showing') || 'Showing'}{' '}
                  <span className={`${FONT_WEIGHTS.semibold} ${COLOR_SCALE.foreground}`}>
                    {startIndex + 1}
                  </span>{' '}
                  {t('admin_inquiries', 'to') || 'to'}{' '}
                  <span className={`font-semibold ${COLOR_SCALE.foreground}`}>
                    {Math.min(endIndex, totalItems)}
                  </span>{' '}
                  {t('admin_inquiries', 'of') || 'of'}{' '}
                  <span className={`font-semibold ${COLOR_SCALE.foreground}`}>{totalItems}</span>{' '}
                  {t('admin_inquiries', 'inquiries') || 'inquiries'}
                </>
              ) : (
                t('common', 'page_of', `Page ${currentPage} of ${totalPages}`, {
                  current: currentPage,
                  total: totalPages,
                })
              )}
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              showFirstLast={showFirstLast}
              showKeyboardHint={showKeyboardHint}
              setShowKeyboardHint={setShowKeyboardHint}
              className={`flex items-center ${GAP_SCALE['2']}`}
            />
          )}
        </div>
      )}

      {/* Pagination Controls for admin variant */}
      {variant === 'admin' && totalPages > 1 && (
        <PaginationButtons
          currentPage={currentPage}
          totalPages={totalPages}
          pageNumbers={Array.from({ length: totalPages }, (_, i) => i + 1)}
          setCurrentPage={page => {
            const pageNumber = typeof page === 'function' ? page(currentPage) : page;
            onPageChange(pageNumber);
          }}
        />
      )}

      {/* Keyboard Shortcut Hint */}
      {showKeyboardHint && totalPages > 1 && variant !== 'admin' && (
        <div
          className={`${MARGIN_TOP['4']} ${PADDING_TOP['4']} border-t ${COLOR_SCALE['border-subtle']}`}
        >
          <div
            className={`flex items-center justify-center ${GAP_SCALE['2']} ${TYPE_SCALE.sm} ${COLOR_SCALE['foreground-muted']}`}
          >
            <span>{t('blog', 'keyboard_hint') || 'Tip: Use ← → arrow keys to navigate pages'}</span>
            <button
              onClick={() => setShowKeyboardHint?.(false)}
              className={`${COLOR_SCALE['icon-muted']} hover:${COLOR_SCALE['foreground-muted']} ${MARGIN_LEFT['2']}`}
              aria-label={t('aria_labels', 'dismiss_hint') || 'Dismiss hint'}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

UnifiedPagination.displayName = 'UnifiedPagination';

export {
  Pagination,
  PaginationContent,
  PaginationLink,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
  PaginationComponentWithMonitor as PaginationComponent,
  UnifiedPagination,
};
